import { PER_PAGE } from '@common/constants/filters'
import { TWILIO_MESSAGE_RECEIVED } from '@common/constants/webSockets'
import { ITwilioMessageRow } from '@common/interfaces/twilio'
import { Action } from 'redux'
import { select, takeEvery } from 'redux-saga/effects'
import { ITwilioMessagesParams } from '../../api/twilio'
import { putPromise } from '../../helpers/redux'
import { ILoadingData } from '../types'
import { listAllMessages, listMessages } from './actions'
import {
  allMessages as allMessagesSelector,
  lastAllMessagesParams as lastAllMessagesParamsSelector,
  lastMessagesParams as lastMessagesParamsSelector,
} from './selectors'

/**
 * Payload of the TWILIO_MESSAGE_RECEIVED websocket event, dispatched as a redux
 * action by webSocketMiddleware. Extends `Action` so `takeEvery` resolves the
 * string-pattern overload, matching IArcNoteWorkingAction in redux/types.
 */
interface ITwilioMessageReceivedAction extends Action {
  data?: {
    messageId?: string
    phoneNumberId?: string | null
    phoneNumber?: string
    channel?: string
  }
}

/**
 * A code just landed on one of the numbers — pull it into the open feed.
 *
 * The broadcast deliberately carries no message body (it fans out to every ops
 * socket, and the codes are only readable through the permission-checked list
 * endpoint), so showing it means refetching. `skipLoader` keeps the feed from
 * flashing a skeleton over rows that are already on screen.
 *
 * Scoped to the feed actually being viewed: the event reaches every connected
 * ops user, and refetching a number nobody has open would be pure noise.
 *
 * Only the feed is refetched. The numbers list carries no per-number message
 * data, so an inbound code cannot make it stale — and refetching it here would
 * have to reproduce whatever search/status filter is applied or it would quietly
 * replace a filtered list with an unfiltered one.
 */
export function* refetchMessagesOnInbound() {
  yield takeEvery(TWILIO_MESSAGE_RECEIVED, function* (action: ITwilioMessageReceivedAction) {
    // Annotated rather than cast: `yield` in an un-annotated generator is `any`,
    // and an `as` cast to this slice's own type reads as redundant to eslint.
    // Same idiom as atlasai/sagas.ts's `const panel: ISubagentStatus[] = yield select(...)`.
    // The list under the numbers table is always on screen where it exists, so
    // it refreshes on every inbound code rather than being gated on a modal.
    const allParams: ITwilioMessagesParams | null = yield select(lastAllMessagesParamsSelector)
    if (allParams) {
      // Asking for as many rows as are loaded, not one page: a refetch without
      // `loadMore` REPLACES the feed (helpers/redux.ts), so a plain page-0
      // request would throw away everything the user had scrolled in — and this
      // fires on every inbound code across the whole pool. One window of the
      // loaded size, taken from the top, keeps what is on screen and brings the
      // new code with it.
      const loaded: ILoadingData<{ data: ITwilioMessageRow[] }> | null =
        yield select(allMessagesSelector)
      const loadedCount = loaded?.data?.data?.length || 0
      yield putPromise(
        listAllMessages({
          ...allParams,
          perPage: Math.max(allParams.perPage || PER_PAGE, loadedCount),
          skipLoader: true,
        }),
      )
    }

    const params: ITwilioMessagesParams | null = yield select(lastMessagesParamsSelector)
    if (!params) {
      return
    }
    // A feed filtered to one number only cares about that number. An unfiltered
    // feed (or one filtered by client) takes every inbound message,
    // since the server decides what belongs in it.
    if (
      params.phoneNumberId &&
      action.data?.phoneNumberId &&
      params.phoneNumberId !== action.data.phoneNumberId
    ) {
      return
    }
    yield putPromise(listMessages({ ...params, skipLoader: true }))
  })
}
